import type { components, paths } from '../types/api/generated';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';

export type TeamStatsResponse = components['schemas']['TeamStatsResponse'];
export type TeamStatsRow = TeamStatsResponse['data'][number];
export type TeamStatsQuery = NonNullable<
  paths['/league/teams/stats']['get']['parameters']['query']
>;
export type TeamStatsAggregation = NonNullable<TeamStatsQuery['aggregation']>;
export type TeamStatsVenue = NonNullable<TeamStatsQuery['venue']>;
export type TeamStatsSortDirection = NonNullable<
  TeamStatsQuery['sortDirection']
>;

export type GetTeamStatsOptions = {
  accessToken?: string;
  fields: string;
  cursor?: string;
  limit?: number;
  aggregation?: TeamStatsAggregation;
  venue?: TeamStatsVenue;
  season?: number | null;
  minGameweek?: number;
  maxGameweek?: number;
  minDifficulty?: number;
  maxDifficulty?: number;
  sortBy?: string;
  sortDirection?: TeamStatsSortDirection;
};

const buildQuery = ({
  fields,
  cursor,
  limit,
  aggregation,
  venue,
  season,
  minGameweek,
  maxGameweek,
  minDifficulty,
  maxDifficulty,
  sortBy,
  sortDirection
}: GetTeamStatsOptions): TeamStatsQuery => ({
  fields,
  ...(cursor !== undefined && { after: cursor }),
  ...(limit !== undefined && { limit }),
  ...(aggregation !== undefined && { aggregation }),
  ...(venue !== undefined && { venue }),
  ...(season !== undefined && season !== null && { season }),
  ...(minGameweek !== undefined && { minGameweek }),
  ...(maxGameweek !== undefined && { maxGameweek }),
  ...(minDifficulty !== undefined && { minDifficulty }),
  ...(maxDifficulty !== undefined && { maxDifficulty }),
  ...(sortBy !== undefined && { sortBy }),
  ...(sortDirection !== undefined && { sortDirection })
});

export const fetchTeamStats = async (
  options: GetTeamStatsOptions
): Promise<TeamStatsResponse> => {
  const { accessToken } = options;
  const { data, error } = await ffhClient().GET('/league/teams/stats', {
    params: { query: buildQuery(options) },
    ...(accessToken ? { headers: authHeader(accessToken) } : {})
  });

  if (error) throw new Error('Failed to load team stats');
  return data;
};
